import { zodResolver } from '@hookform/resolvers/zod';
import { Search } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/Select';
import { useSessionStorage } from '@/hooks/useSessionStorage';
import { SearchFilter } from '@/services/ProductsService';

const searchSchema = z.object({
  searchTerm: z.string(),
  filterBy: z.enum(['productName', 'productId', 'productSku']),
});

type SearchFormData = z.infer<typeof searchSchema>;

interface SearchProductsFormProps {
  searchFilters: Partial<SearchFilter>;
  onSearchFilters: <K extends keyof SearchFilter>(
    filterKey: K,
    filterValue: SearchFilter[K] | undefined,
  ) => void;
}

export function SearchProductsForm({
  searchFilters,
  onSearchFilters,
}: SearchProductsFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'components.SearchProductsForm',
  });

  const [filterBy, setFilterBy] = useSessionStorage<
    'productName' | 'productId' | 'productSku'
  >('filterBy', 'productName');

  const {
    formState: { isValid, errors, isSubmitting },
    control,
    register,
    handleSubmit,
    reset,
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      filterBy,
    },
  });

  const isValidForm = isValid && !isSubmitting;

  const hasSearchFilters =
    !!searchFilters.productId ||
    !!searchFilters.productName ||
    !!searchFilters.productSku;

  function handleResetSearchFilters(resetSearchTerm: boolean = false) {
    onSearchFilters('productId', undefined);
    onSearchFilters('productName', undefined);
    onSearchFilters('productSku', undefined);

    if (resetSearchTerm) {
      reset();
    }
  }

  const onSubmit = handleSubmit((data: SearchFormData) => {
    handleResetSearchFilters();
    onSearchFilters(data.filterBy, data.searchTerm);
  });

  return (
    <div className="mb-4">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <FormGroup
            error={{ message: errors.searchTerm?.message, font: 'sm' }}
          >
            <Input
              id="searchTerm"
              className="w-full"
              placeholder={t('searchPlaceholder')}
              {...register('searchTerm')}
            />
          </FormGroup>
        </div>

        <FormGroup error={{ message: errors.filterBy?.message, font: 'sm' }}>
          <Controller
            name="filterBy"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  setFilterBy(
                    value as 'productName' | 'productId' | 'productSku',
                  );
                }}
                defaultValue={field.value}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('select.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="productName">
                    {t('select.productName')}
                  </SelectItem>
                  <SelectItem value="productId">
                    {t('select.productId')}
                  </SelectItem>
                  <SelectItem value="productSku">
                    {t('select.productSku')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormGroup>

        <Button
          type="submit"
          className="flex items-center gap-2"
          disabled={!isValidForm}
        >
          <Search className={`h-4 w-4 ${isSubmitting ? 'animate-spin' : ''}`} />
          {isSubmitting ? t('searchingButton') : t('searchButton')}
        </Button>

        {hasSearchFilters && (
          <Button
            type="button"
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => handleResetSearchFilters(true)}
          >
            Limpar
          </Button>
        )}
      </form>
    </div>
  );
}
